/**
 * Server-sent event parsing.
 *
 * Isolated from the transport so it can be tested without a network: feed it
 * arbitrary chunk boundaries and assert the events that fall out. The reference
 * implementation's parser only tolerated a split at the very end of a buffer;
 * this one is a proper incremental parser, because a token stream splits
 * wherever the network decides.
 */

/** Fields of one SSE event, per the spec's line grammar. */
export interface RawEvent {
  data: string;
}

export class SseParser {
  private buffer = '';
  private dataLines: string[] = [];

  /**
   * Feed a decoded chunk; returns whatever events completed.
   *
   * Events terminate on a blank line. Lines beginning `:` are comments (often
   * used as keep-alives) and are skipped. Only `data:` is retained — this
   * protocol carries its whole payload there.
   */
  push(chunk: string): RawEvent[] {
    this.buffer += chunk;
    const events: RawEvent[] = [];

    // Normalise CRLF so a proxy that rewrites line endings cannot break framing.
    this.buffer = this.buffer.replace(/\r\n/g, '\n');

    let newlineAt = this.buffer.indexOf('\n');
    while (newlineAt !== -1) {
      const line = this.buffer.slice(0, newlineAt);
      this.buffer = this.buffer.slice(newlineAt + 1);

      if (line === '') {
        const completed = this.takeEvent();
        if (completed) events.push(completed);
      } else if (!line.startsWith(':')) {
        const colon = line.indexOf(':');
        const field = colon === -1 ? line : line.slice(0, colon);
        if (field === 'data') {
          // A single leading space after the colon is part of the framing,
          // not the payload.
          let value = colon === -1 ? '' : line.slice(colon + 1);
          if (value.startsWith(' ')) value = value.slice(1);
          this.dataLines.push(value);
        }
      }

      newlineAt = this.buffer.indexOf('\n');
    }

    return events;
  }

  /**
   * Flush at end-of-stream. A well-behaved server ends with a blank line, but a
   * truncated final event is still worth surfacing rather than discarding.
   */
  flush(): RawEvent[] {
    if (this.buffer.length > 0) {
      const trailing = this.buffer;
      this.buffer = '';
      if (!trailing.startsWith(':')) {
        const colon = trailing.indexOf(':');
        if (trailing.slice(0, colon === -1 ? trailing.length : colon) === 'data') {
          let value = colon === -1 ? '' : trailing.slice(colon + 1);
          if (value.startsWith(' ')) value = value.slice(1);
          this.dataLines.push(value);
        }
      }
    }
    const completed = this.takeEvent();
    return completed ? [completed] : [];
  }

  private takeEvent(): RawEvent | null {
    if (this.dataLines.length === 0) return null;
    // Multi-line data is joined with newlines, per the SSE spec.
    const data = this.dataLines.join('\n');
    this.dataLines = [];
    return { data };
  }
}
